import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Add from '@mui/icons-material/Add';
import ChevronRight from '@mui/icons-material/ChevronRight';
import TimerIcon from '@mui/icons-material/Timer';
import { grey } from '@mui/material/colors';

const LS_KEY = '__app_quickscore';

export default function QuickScore() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState(() => {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (data !== null) {
      localStorage.setItem(LS_KEY, JSON.stringify(data));
    }
  }, [data]);

  function onNewMatch() {
    const index = (data || []).length;
    const newMatch = {
      id: uuidv4(),
      title: t('app.qsNewMatchTitle', { num: index + 1 }),
      created: Date.now(),
      stages: [],
    };
    const newData = [...(data || []), newMatch];
    localStorage.setItem(LS_KEY, JSON.stringify(newData));
    setData(newData);
    navigate('/app/quickscore/' + index);
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 'calc(100vh - 120px)', bgcolor: '#f5f5f5',
      }}>
        <Box sx={{ textAlign: 'center', width: '90%', maxWidth: 400 }}>
          <Box sx={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'linear-gradient(135deg, #1976d2, #0d47a1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 3, boxShadow: 3,
          }}>
            <TimerIcon sx={{ color: 'white', fontSize: 40 }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {t('app.qsTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
            {t('app.qsDescription')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onNewMatch}
            sx={{ borderRadius: 2.5, py: 1.5, px: 4, textTransform: 'none', fontWeight: 600 }}
          >
            {t('app.qsNewMatch')}
          </Button>
        </Box>
      </Box>
    );
  }

  // Match list
  return (
    <Box sx={{ pb: '80px', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Hero header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
        color: 'white',
        px: 2, pt: 2, pb: 3,
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('app.qsTitle')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
          {data.length} {data.length === 1 ? 'match' : 'matches'}
        </Typography>
      </Box>

      {/* Match cards */}
      <Box sx={{ px: 1.5, mt: -1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {data.map((match, index) => {
          const stageCount = (match.stages || []).length;
          return (
            <Paper key={match.id} sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              <ButtonBase
                onClick={() => navigate('/app/quickscore/' + index)}
                sx={{ p: 0, textAlign: 'start', width: '100%', display: 'flex', alignItems: 'center' }}
              >
                {/* Color accent */}
                <Box sx={{
                  width: 4, alignSelf: 'stretch',
                  background: 'linear-gradient(to bottom, #1976d2, #0d47a1)',
                }} />
                <Box sx={{ flex: 1, p: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {match.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {moment(match.created).format('L')}
                    </Typography>
                    <Box sx={{
                      bgcolor: stageCount > 0 ? 'primary.main' : grey[300],
                      color: stageCount > 0 ? 'white' : 'text.secondary',
                      borderRadius: 1, px: 1, py: 0.125,
                      fontSize: 11, fontWeight: 700,
                    }}>
                      {stageCount} {t('app.qsStages').toLowerCase()}
                    </Box>
                  </Box>
                </Box>
                <ChevronRight sx={{ color: grey[400], mr: 1 }} />
              </ButtonBase>
            </Paper>
          );
        })}

        {/* New match button */}
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onNewMatch}
          sx={{
            borderRadius: 2.5, py: 1.5, mt: 0.5,
            textTransform: 'none', fontWeight: 600,
          }}
        >
          {t('app.qsNewMatch')}
        </Button>
      </Box>
    </Box>
  );
}
